import { useState, useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { motion, AnimatePresence } from "framer-motion";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Lightning } from "@phosphor-icons/react";

function getSubmitLabel(loading, mode) {
  if (loading) return "Please wait...";
  if (mode === "login") return "Sign In";
  return "Create Account";
}

const FADE_IN = { opacity: 0, y: 30 };
const FADE_IN_END = { opacity: 1, y: 0 };
const FADE_TRANSITION = { duration: 0.5 };
const COLLAPSE_INITIAL = { opacity: 0, height: 0 };
const COLLAPSE_ANIMATE = { opacity: 1, height: "auto" };
const FADE_OPACITY = { opacity: 0 };
const FADE_OPACITY_END = { opacity: 1 };

export default function LoginPage() {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    let result;
    if (mode === "login") {
      result = await login(email, password);
    } else {
      if (!name.trim()) {
        setError("Name is required");
        setLoading(false);
        return;
      }
      result = await register(name, email, password);
    }

    if (!result.success) {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <div className="dark">
      <div className="min-h-screen bg-black text-white flex items-center justify-center p-4">
        <motion.div
          initial={FADE_IN}
          animate={FADE_IN_END}
          transition={FADE_TRANSITION}
          className="w-full max-w-md"
        >
          {/* Brand */}
          <div className="text-center mb-10">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Lightning size={40} weight="duotone" className="text-white" />
              <h1 className="text-4xl font-bold tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }} data-testid="login-brand-title">
                BusyBodies AI
              </h1>
            </div>
            <p className="text-sm text-neutral-400" data-testid="login-brand-subtitle">
              Synthetic General Intelligence for Everything
            </p>
          </div>

          {/* Card */}
          <div className="border border-neutral-800 bg-neutral-950 p-8" data-testid="auth-card">
            {/* Tab toggle */}
            <div className="flex mb-8 border border-neutral-800" data-testid="auth-mode-toggle">
              <button
                onClick={() => { setMode("login"); setError(""); }}
                className={`flex-1 py-3 text-sm font-medium transition-colors ${
                  mode === "login"
                    ? "bg-white text-black"
                    : "text-neutral-400 hover:text-white"
                }`}
                data-testid="auth-login-tab"
              >
                Sign In
              </button>
              <button
                onClick={() => { setMode("register"); setError(""); }}
                className={`flex-1 py-3 text-sm font-medium transition-colors ${
                  mode === "register"
                    ? "bg-white text-black"
                    : "text-neutral-400 hover:text-white"
                }`}
                data-testid="auth-register-tab"
              >
                Create Account
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <AnimatePresence mode="wait">
                {mode === "register" && (
                  <motion.div
                    key="name"
                    initial={COLLAPSE_INITIAL}
                    animate={COLLAPSE_ANIMATE}
                    exit={COLLAPSE_INITIAL}
                  >
                    <label className="block text-xs text-neutral-400 mb-2 uppercase tracking-wider">Name</label>
                    <Input
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Your name"
                      className="bg-transparent border-neutral-700 focus:border-white"
                      data-testid="auth-name-input"
                    />
                  </motion.div>
                )}
              </AnimatePresence>

              <div>
                <label className="block text-xs text-neutral-400 mb-2 uppercase tracking-wider">Email</label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="bg-transparent border-neutral-700 focus:border-white"
                  required
                  data-testid="auth-email-input"
                />
              </div>

              <div>
                <label className="block text-xs text-neutral-400 mb-2 uppercase tracking-wider">Password</label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  className="bg-transparent border-neutral-700 focus:border-white"
                  required
                  data-testid="auth-password-input"
                />
              </div>

              {error && (
                <motion.p
                  initial={FADE_OPACITY}
                  animate={FADE_OPACITY_END}
                  className="text-red-400 text-sm"
                  data-testid="auth-error-message"
                >
                  {error}
                </motion.p>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-white text-black hover:bg-neutral-200 h-12 text-sm font-semibold"
                data-testid="auth-submit-button"
              >
                {getSubmitLabel(loading, mode)}
              </Button>
            </form>
          </div>

          <p className="text-center text-xs text-neutral-600 mt-6">
            busybodiesai.com
          </p>
        </motion.div>
      </div>
    </div>
  );
}
